class ShippingBar extends HTMLElement {
    constructor() {
      super();
      
      // Get currency conversion rate and format from Shopify
      this.conversionRate = window.Shopify?.currency?.rate || 1;
      this.activeCurrency = window.Shopify?.currency?.active || 'USD';
      this.moneyFormat = window.theme?.settings?.money_with_currency_format || '{{ amount }}';
      
      // Get locale for fallback formatting
      this.locale = document.documentElement.lang || 'en';
      
      // Track initialization state
      this.isInitialized = false;
      this.cartUpdateHandler = null;
      this.currencyChangeHandler = null;
    }
  
    connectedCallback() {
      // Check initial cart state and initialize if needed
      this.checkCartStateAndInitialize();
    }

    disconnectedCallback() {
      this.#cleanup();
    }

    // Public method that cart drawer can call
    async checkCartStateAndInitialize() {
      try {
        const response = await fetch('/cart.js');
        const cartData = await response.json();
        const { item_count } = cartData;

        if (item_count > 0 && !this.isInitialized) {
          this.#initialize();
        } else if (item_count === 0 && this.isInitialized) {
          this.#disconnect();
        }
      } catch (error) {
        console.error('Error checking cart state:', error);
      }
    }

    #initialize() {
      if (this.isInitialized) return;
      
      this.isInitialized = true;
      this.classList.remove('shipping-bar--disconnected');
      
      this.#setupStages();
      this.updateShippingBar();
      
      // Set up event listeners
      this.cartUpdateHandler = (event) => this.#handleCartUpdate(event);
      document.addEventListener('cart:updated', this.cartUpdateHandler);
      
      if (typeof pubsub !== 'undefined' && pubsub.subscribe) {
        pubsub.subscribe('variant:change', () => this.updateShippingBar());
      }

      this.currencyChangeHandler = () => {
        this.conversionRate = window.Shopify?.currency?.rate || 1;
        this.activeCurrency = window.Shopify?.currency?.active || 'USD';
        this.#setupStages();
        this.updateShippingBar();
      };
      document.addEventListener('currency:changed', this.currencyChangeHandler);
    }

    #disconnect() {
      if (!this.isInitialized) return;
      
      this.isInitialized = false;
      this.classList.add('shipping-bar--disconnected');
      
      this.#cleanup();
    }

    #cleanup() {
      // Remove event listeners
      if (this.cartUpdateHandler) {
        document.removeEventListener('cart:updated', this.cartUpdateHandler);
        this.cartUpdateHandler = null;
      }
      
      if (this.currencyChangeHandler) {
        document.removeEventListener('currency:changed', this.currencyChangeHandler);
        this.currencyChangeHandler = null;
      }
      
      // Reset progress bar
      const progressInner = this.querySelector('[data-progress-line]');
      if (progressInner) {
        progressInner.style.width = '0%';
      }
    }

    async #handleCartUpdate(event) {
      // Don't react to cart updates from our own initialization
      if (event?.detail?.source === 'shipping-bar') return;
      
      // Check if cart state changed between empty/non-empty
      try {
        const response = await fetch('/cart.js');
        const cartData = await response.json();
        const { item_count } = cartData;

        if (item_count > 0 && !this.isInitialized) {
          this.#initialize();
        } else if (item_count === 0 && this.isInitialized) {
          this.#disconnect();
        } else if (item_count > 0 && this.isInitialized) {
          // Just update the existing shipping bar
          this.updateShippingBar();
        }
      } catch (error) {
        console.error('Error handling cart update:', error);
      }
    }
  
    #setupStages() {
      const stages = this.#getStages();
      this.stages = stages.filter(stage => stage.threshold > 0).sort((a, b) => a.threshold - b.threshold);
    }
  
    #getStages() {
      const stages = [];
      
      // Get up to 3 stages from data attributes
      for (let i = 1; i <= 3; i++) {
        const threshold = parseFloat(this.dataset[`stage${i}Threshold`]);
        const text = this.dataset[`stage${i}Text`];
        const icon = this.dataset[`stage${i}Icon`];
        
        if (threshold && text) {
          stages.push({
            // Threshold is already in cents from Liquid template
            // Apply currency conversion to convert from shop base currency to customer's currency
            threshold: Math.round(threshold * this.conversionRate),
            text: text,
            icon: icon || 'checkmark'
          });
        }
      }
      
      return stages;
    }
  
    async updateShippingBar() {
      if (!this.isInitialized || !this.stages.length) return;
  
      try {
        const response = await fetch('/cart.js');
        const cartData = await response.json();
        
        // total_price from /cart.js is in cents and already in customer's currency
        const { total_price, item_count } = cartData;
  
        // If cart becomes empty, disconnect
        if (item_count === 0) {
          this.#disconnect();
          return;
        }
  
        // Debug logging (remove in production)
        if (window.Shopify?.designMode) {
          console.log('Shipping Bar Debug:', {
            cartTotal: total_price,
            cartTotalFormatted: this.#formatMoney(total_price),
            activeCurrency: this.activeCurrency,
            conversionRate: this.conversionRate,
            stages: this.stages,
            nextStage: this.#getNextStage(total_price),
            currentStage: this.#getCurrentStage(total_price)
          });
        }
  
        this.#updateProgressAndText(total_price);
      } catch (error) {
        console.error('Error fetching cart data:', error);
      }
    }
  
    #updateProgressAndText(cartTotal) {
      const currentStage = this.#getCurrentStage(cartTotal);
      const nextStage = this.#getNextStage(cartTotal);
      
      const textElement = this.querySelector('.progress-bar__text');
      const progressInner = this.querySelector('[data-progress-line]');

      if (!textElement || !progressInner) {
        console.warn('Shipping bar elements not found:', { textElement, progressInner });
        return;
      }

      // Calculate progress and update text
      if (nextStage) {
        const remainingAmount = nextStage.threshold - cartTotal;
        const progressPercent = this.#calculateProgress(cartTotal);
        
        // Replace {{ amount }} placeholder in the text with the formatted remaining amount
        const displayText = nextStage.text.replace('{{ amount }}', this.#formatMoney(remainingAmount));
        
        textElement.innerHTML = 'Spend ' + `<strong>${this.#formatMoney(remainingAmount)}</strong>` + ' more to receive ' + `<strong>${displayText}</strong>`;
        progressInner.style.width = `${Math.min(progressPercent, 100)}%`;
      } else {
        // All thresholds reached - show completion message
        const lastStage = this.stages[this.stages.length - 1];
        const displayText = lastStage.text.replace('{{ amount }}', this.#formatMoney(0));
        
        textElement.innerHTML = `🎉 ${displayText}`;
        progressInner.style.width = '100%';

        const cartShipping = document.querySelector(".cart-drawer__summary-shipping");
        cartShipping.style="display: flex;"
      }

      // Update stage indicators
      this.#updateStageIndicators(cartTotal);
    }
  
    #getCurrentStage(cartTotal) {
      // Find the highest threshold that cart total has reached
      for (let i = this.stages.length - 1; i >= 0; i--) {
        if (cartTotal >= this.stages[i].threshold) {
          return this.stages[i];
        }
      }
      return null; // Cart hasn't reached any threshold yet
    }
  
    #getNextStage(cartTotal) {
      // Find the first threshold that cart total hasn't reached yet
      return this.stages.find(stage => cartTotal < stage.threshold) || null;
    }
  
    #calculateProgress(cartTotal) {
      const maxThreshold = Math.max(...this.stages.map(s => s.threshold));
      
      // Calculate progress as percentage of max threshold to align with milestone positioning
      const progressPercent = Math.min((cartTotal / maxThreshold) * 100, 100);
      
      return progressPercent;
    }
  
    #updateStageIndicators(cartTotal) {
      const indicators = this.querySelectorAll('.shipping-bar__milestone');
      
      indicators.forEach((indicator, index) => {
        const stage = this.stages[index];
        if (!stage) return;
  
        const isCompleted = cartTotal >= stage.threshold;
        const isCurrent = !isCompleted && (!this.stages[index + 1] || cartTotal < this.stages[index + 1].threshold);
  
        indicator.classList.toggle('shipping-bar__milestone--completed', isCompleted);
        indicator.classList.toggle('shipping-bar__milestone--current', isCurrent);
      });
    }
  
    #formatMoney(amount) {
      // Use global formatPrice function if available (preferred)
      if (typeof formatPrice === 'function' && this.moneyFormat) {
        // formatPrice expects amount in dollars, convert from cents
        return formatPrice(this.moneyFormat, amount / 100);
      }
      
      // Fallback: Use Shopify's formatMoney if available
      if (window.Shopify && window.Shopify.formatMoney) {
        return window.Shopify.formatMoney(amount);
      }
      
      // Final fallback: Use Intl.NumberFormat with customer's currency and locale
      const amountInDollars = amount / 100;
      return new Intl.NumberFormat(this.locale, {
        style: 'currency',
        currency: this.activeCurrency
      }).format(amountInDollars);
    }
  }
  
  customElements.define('shipping-bar', ShippingBar);